import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  description: string;
  type: 'destructive' | 'warning' | 'default';
  confirmLabel: string;
  cancelLabel: string;
  loading: boolean;
  instantClose: boolean;
  // Internal — resolve function for the Promise
  _resolve: ((confirmed: boolean) => void) | null;
}

interface ConfirmDialogActions {
  _confirm: () => void;
  _cancel: () => void;
  setLoading: (loading: boolean) => void;
}

const initialState: ConfirmDialogState = {
  isOpen: false,
  title: '',
  description: '',
  type: 'default',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  loading: false,
  instantClose: false,
  _resolve: null,
};

export const useConfirmDialogStore = create<ConfirmDialogState & ConfirmDialogActions>()(
  devtools(
    (set) => ({
      ...initialState,

      _confirm: () =>
        set(
          (state) => {
            state._resolve?.(true);
            return { isOpen: false, _resolve: null, loading: false };
          },
          false,
          '_confirm'
        ),

      _cancel: () =>
        set(
          (state) => {
            state._resolve?.(false);
            return { isOpen: false, _resolve: null, loading: false };
          },
          false,
          '_cancel'
        ),

      setLoading: (loading) => set({ loading }, false, 'setLoading'),
    }),
    { name: 'ConfirmDialogStore', enabled: import.meta.env.DEV }
  )
);

export async function openConfirmDialog(config: {
  title: string;
  description?: string;
  type?: 'destructive' | 'warning' | 'default';
  confirmLabel?: string;
  cancelLabel?: string;
  instantClose?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    useConfirmDialogStore.getState()._resolve?.(false);
    useConfirmDialogStore.setState({
      isOpen: true,
      title: config.title,
      description: config.description ?? '',
      type: config.type ?? 'default',
      confirmLabel: config.confirmLabel ?? 'Confirm',
      cancelLabel: config.cancelLabel ?? 'Cancel',
      loading: false,
      instantClose: config.instantClose ?? false,
      _resolve: resolve,
    });
  });
}

// Selectors
export const useConfirmDialog = () =>
  useConfirmDialogStore(
    useShallow((state) => ({
      isOpen: state.isOpen,
      title: state.title,
      description: state.description,
      type: state.type,
      confirmLabel: state.confirmLabel,
      cancelLabel: state.cancelLabel,
      loading: state.loading,
      instantClose: state.instantClose,
    }))
  );

// Actions (for the ConfirmDialog component)
export const useConfirmDialogActions = () =>
  useConfirmDialogStore(
    useShallow((state) => ({
      _confirm: state._confirm,
      _cancel: state._cancel,
      setLoading: state.setLoading,
    }))
  );
